import React, { useState, useRef, useEffect } from 'react'
import {
  FileText,
  Upload,
  Eye,
  X,
  Clipboard,
  Copy,
  Plus,
  Trash2,
  ChevronRight,
  Settings,
  Save,
  Check,
  Package,
  Undo,
  Redo,
  Maximize2,
  Import,
  Layers,
  Presentation,
  Palette,
  Zap,
  EyeOff,
  Globe,
  CheckCircle,
  XCircle,
  RefreshCw,
  Grid3X3,
  History,
  User,
  Shield,
  ListChecks,
  Pencil
} from 'lucide-react'


const EditorNavigation = ({
  // File menu props
  showFileMenu,
  setShowFileMenu,
  handleSave,
  navigate,
  handleDownload,
  template_content,
  settemplate_content,
  setHasUnsavedChanges,
  templateAPI,
  templateMetadata,
  onRenameTemplate,
  onAfterResize,

  // Import menu props
  showImportMenu,
  setShowImportMenu,
  handleImportPDF,
  handleImportPSD,
  handleImportPowerPoint,
  handleImportCDR,
  handleImportLegacyPlatform,

  // Video export props
  handleVideoExport,
  isExportingVideo,
  showVideoExportDialog,
  setShowVideoExportDialog,
  setShowDurationScaleDialog,
  onBulkGenerateAIImages,

  // Grid and settings props
  showGrid,
  setShowGrid,
  showMouseCoords,
  setShowMouseCoords,
  showPropertiesBarAtBottom,
  setShowPropertiesBarAtBottom,
  showVideoExportProgress,
  setShowVideoExportProgress,
  showRulers,
  setShowRulers,
  showFooterBar,
  setShowFooterBar,
  isFullscreen,
  setIsFullscreen,

  // Undo/Redo props
  undo,
  redo,
  historyIndex,
  history,

  // Save props
  saveTemplate,
  isSaving,
  isSaved,
  hasUnsavedChanges,

  // PowerClip props
  powerClipMode,
  setPowerClipMode,
  setPowerClipContent,

  // Share props
  showShareDialog,
  setShowShareDialog,
  shareDropdownRef,
  copyLinkToClipboard,
  handleDownloadCurrent,
  handleDownloadFull,
  handleTifDownloadCurrent,
  handleTifDownloadFull,
  handleDownloadCurrentWithColorMode,
  handleDownloadFullWithColorMode,
  isDownloading,
  downloadingStates = {},

  // Export Progress panel toggle (adjacent to Export button)
  isExportProgressExpanded,
  onToggleExportProgress,
  exportProgressJobCount = 0,
  exportProgressActiveCount = 0,

  // Upload props
  onUploadClick,

  // Resize props
  currentPageIndex,
  currentPage,

  // Action menu props
  showActionMenu,
  setShowActionMenu,
  handleMakePrivate,
  handleMakePublic,
  handleGenerateSample,
  handleApprove,
  handleReject,
  handleCloneAndOpen,
  handleTriggerSync,
  isAdmin,

  // View menu props
  showViewMenu,
  setShowViewMenu,
  handleGenerate9Samples,
  showSampleGenerationDialog,
  setShowSampleGenerationDialog,
  showPastSamplesDialog,
  setShowPastSamplesDialog,
  showAiQueueDialog,
  setShowAiQueueDialog
}) => {
  // State for exit confirmation dialog
  const [showExitConfirmation, setShowExitConfirmation] = useState(false)

  // Inline template-name editing (Task 2)
  const currentTemplateName = templateMetadata?.name || template_content?.name || 'Untitled Template'
  const [isEditingName, setIsEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState(currentTemplateName)
  const [savingName, setSavingName] = useState(false)
  const nameInputRef = useRef(null)

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [isEditingName])

  const beginEditName = () => {
    if (!onRenameTemplate) return
    setNameDraft(currentTemplateName)
    setIsEditingName(true)
  }

  const commitEditName = async () => {
    const next = (nameDraft || '').trim()
    if (!next || next === currentTemplateName) {
      setIsEditingName(false)
      return
    }
    setSavingName(true)
    try {
      const result = await onRenameTemplate(next)
      if (result?.success !== false) setIsEditingName(false)
    } finally {
      setSavingName(false)
    }
  }

  const cancelEditName = () => {
    setNameDraft(currentTemplateName)
    setIsEditingName(false)
  }

  // State for resize dialog
  const [showResizeDialog, setShowResizeDialog] = useState(false)
  const [resizeConfig, setResizeConfig] = useState({
    width: 800,
    height: 600,
    maintainAspectRatio: true
  })

  // Handle exit with confirmation
  const handleExit = () => {
    if (hasUnsavedChanges) {
      setShowExitConfirmation(true)
    } else {
      navigate('/dashboard')
    }
  }

  // Handle confirmed exit with save
  const handleConfirmedExitWithSave = async () => {
    setShowExitConfirmation(false)
    await saveTemplate(true) // Use the same save function as the top nav save button
    navigate('/dashboard')
  }

  // Handle confirmed exit without save
  const handleConfirmedExitWithoutSave = () => {
    setShowExitConfirmation(false)
    navigate('/dashboard')
  }

  // Handle resize page
  const handleResizePage = () => {
    if (currentPage?.page_config) {
      setResizeConfig({
        width: currentPage.page_config.width || 800,
        height: currentPage.page_config.height || 600,
        maintainAspectRatio: true
      })
    }
    setShowResizeDialog(true)
    setShowFileMenu(false)
  }

  // Handle resize confirmation
  const handleConfirmResize = () => {
    const oldWidth = currentPage?.page_config?.width || 800
    const oldHeight = currentPage?.page_config?.height || 600
    const newWidth = Math.max(1, parseInt(resizeConfig.width) || 1)
    const newHeight = Math.max(1, parseInt(resizeConfig.height) || 1)

    // Calculate aspect ratios
    const scaleX = newWidth / oldWidth
    const scaleY = newHeight / oldHeight

    // Update template content with new page dimensions and scaled items
    settemplate_content(prev => ({
      ...prev,
      pages: prev.pages.map((page, index) =>
        index === currentPageIndex
          ? {
            ...page,
            page_config: {
              ...page.page_config,
              width: newWidth,
              height: newHeight
            },
            items: page.items.map(item => ({
              ...item,
              // Scale position
              x: item.x * scaleX,
              y: item.y * scaleY,
              // Scale dimensions
              width: item.width * scaleX,
              height: item.height * scaleY,
              // Scale font size for text elements
              fontSize: item.type === 'text' ? (item.fontSize || 16) * Math.min(scaleX, scaleY) : item.fontSize,
              // Scale PowerClip contents if present
              powerClipContents: item.powerClipContents ? item.powerClipContents.map(content => ({
                ...content,
                x: content.x * scaleX,
                y: content.y * scaleY,
                width: content.width * scaleX,
                height: content.height * scaleY,
                fontSize: content.type === 'text' ? (content.fontSize || 16) * Math.min(scaleX, scaleY) : content.fontSize
              })) : item.powerClipContents
            }))
          }
          : page
      )
    }))

    setShowResizeDialog(false)
    setHasUnsavedChanges(true)
    // Auto-fit the (possibly much larger/smaller) page into view after resize so the
    // workspace stays usable and controls remain visible.
    if (typeof onAfterResize === 'function') {
      setTimeout(() => onAfterResize(), 100)
    }
  }

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-2 sm:px-4 py-[2.8px] flex items-center justify-between shadow-lg border-b border-blue-800">
      <div className="flex items-center space-x-2 sm:space-x-4">


        <div className="flex items-center space-x-1 sm:space-x-2">
          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded"></div>
          </div>
          <span className="font-semibold text-sm sm:text-base hidden sm:inline">{(template_content?.type || template_content?.mediaType || 'image') === 'video' ? 'Video Editor' : 'Image Editor'}</span>
          <span className="font-semibold text-sm sm:hidden">{(template_content?.type || template_content?.mediaType || 'image') === 'video' ? 'Video' : 'Image'}</span>
        </div>

        <nav className="flex items-center space-x-1 sm:space-x-4">
          <div className="relative file-menu-container">
            <button
              onClick={() => setShowFileMenu(!showFileMenu)}
              className="flex items-center space-x-1 hover:bg-blue-600 hover:bg-opacity-50 px-3 py-1 rounded transition-all duration-200"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-5L9 2H4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs">File</span>
              <svg className={`w-3 h-3 transition-transform duration-200 ${showFileMenu ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* File Menu Dropdown */}
            {showFileMenu && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-[600]">
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowFileMenu(false)
                      handleSave().finally(() => navigate('/templates/create'))
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                    <span>Create new design</span>
                  </button>

                  <button
                    onClick={() => {
                      setShowFileMenu(false)
                      if (onUploadClick) {
                        onUploadClick()
                      }
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Upload className="w-4 h-4 text-gray-600" />
                    <span>Upload photos</span>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  <div className="group relative">
                    <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700">
                      <Settings className="w-4 h-4 text-gray-600" />
                      <span>Settings</span>
                      <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                    </button>
                    <div className="hidden group-hover:block absolute top-0 left-full ml-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-[650]">
                      <div className="py-2">
                        <button onClick={() => { setShowGrid(!showGrid); setShowFileMenu(false) }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between text-gray-700">
                          <span>{showGrid ? 'Hide grid' : 'Show grid'}</span>
                          <input type="checkbox" readOnly checked={showGrid} />
                        </button>
                        <button onClick={() => { setShowMouseCoords(!showMouseCoords); setShowFileMenu(false) }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between text-gray-700">
                          <span>Show mouse coordinates</span>
                          <input type="checkbox" readOnly checked={showMouseCoords} />
                        </button>
                        <button onClick={() => { setShowPropertiesBarAtBottom(!showPropertiesBarAtBottom); setShowFileMenu(false) }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between text-gray-700">
                          <span>Show properties bar at bottom</span>
                          <input type="checkbox" readOnly checked={showPropertiesBarAtBottom} />
                        </button>
                        <button onClick={() => { setShowVideoExportProgress(!showVideoExportProgress); setShowFileMenu(false) }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between text-gray-700">
                          <span>Show video export progress</span>
                          <input type="checkbox" readOnly checked={showVideoExportProgress} />
                        </button>
                        {setShowRulers && (
                          <button onClick={() => { setShowRulers(!showRulers); setShowFileMenu(false) }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between text-gray-700">
                            <span>{showRulers ? 'Hide rulers' : 'Show rulers'}</span>
                            <input type="checkbox" readOnly checked={showRulers} />
                          </button>
                        )}
                        {setShowFooterBar && (
                          <button onClick={() => { setShowFooterBar(!showFooterBar); setShowFileMenu(false) }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between text-gray-700">
                            <span>{showFooterBar ? 'Hide footer bar' : 'Show footer bar'}</span>
                            <input type="checkbox" readOnly checked={showFooterBar} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={async () => {
                      setShowFileMenu(false)
                      const current = (template_content?.type || template_content?.mediaType || 'image')
                      const target = current === 'video' ? 'image' : 'video'
                      if (!window.confirm(`Convert this template from ${current} to ${target}?`)) return

                      try {
                        // Update template type in database first
                        const templateId = templateMetadata?.id
                        if (templateId && templateAPI) {
                          console.log(`Updating template ${templateId} type from ${current} to ${target}`)
                          const response = await templateAPI.updateType(templateId, target)
                          console.log('Template type updated successfully:', response.data)
                        } else if (!templateId) {
                          console.warn('⚠️ Cannot update template type: template ID not found. Template may not be saved yet.')
                        }

                        // Minimal conversion: toggle type and ensure video fields exist
                        const next = { ...template_content, type: target }
                        if (target === 'video') {
                          next.timeline = next.timeline || { tracks: [], audio: [] }
                          // Add base timing to each item if missing
                          next.pages = (next.pages || []).map(p => ({
                            ...p,
                            items: (p.items || []).map((it, idx) => ({
                              ...it,
                              time: it.time || { start: idx * 0.1, duration: 2 }
                            }))
                          }))
                        } else {
                          // Remove timeline for image (non-destructive keep in hidden field if needed)
                          next.timeline = next.timeline || undefined
                        }
                        settemplate_content(next)
                        setHasUnsavedChanges(true)

                        // Show success message
                        if (template_content?.id) {
                          alert(`Template successfully converted to ${target}!`)
                        }
                      } catch (error) {
                        console.error('Error updating template type:', error)
                        alert(`Failed to update template type in database: ${error.message || 'Unknown error'}. Please try again.`)
                      }
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <div className="w-4 h-4 text-gray-600">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a3 3 0 013-3h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H5a3 3 0 01-3-3V6zM5 6a1 1 0 000 2v6a1 1 0 001 1h6a1 1 0 001-1V8a1 1 0 100-2H5z" />
                      </svg>
                    </div>
                    <span>Convert to {(template_content?.type || template_content?.mediaType || 'image') === 'video' ? 'Image' : 'Video'}</span>
                  </button>

                  <button
                    onClick={handleResizePage}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Maximize2 className="w-4 h-4 text-gray-600" />
                    <span>Resize Page</span>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={() => {
                      setShowFileMenu(false)
                      handleSave()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Save className="w-4 h-4 text-gray-600" />
                    <span>Save</span>
                    <span className="ml-auto text-sm text-gray-500">All changes saved</span>
                  </button>

                  <button
                    onClick={async () => {
                      setShowFileMenu(false)
                      try {
                        await handleSave()
                        const newName = `${template_content?.name || 'Untitled Template'} copy`
                        const res = await templateAPI.create({
                          name: newName,
                          pages: template_content?.pages,
                          width: template_content?.pages?.[0]?.page_config?.width || 800,
                          height: template_content?.pages?.[0]?.page_config?.height || 600,
                          content: { ...template_content, name: newName }
                        })
                        const newId = res.data?.data?.id
                        if (newId) navigate(`/editor/${newId}`)
                      } catch (e) {
                        console.error('Make a copy failed', e)
                      }
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Copy className="w-4 h-4 text-gray-600" />
                    <span>Make a copy</span>
                  </button>

                  <button
                    onClick={() => {
                      setShowFileMenu(false)
                      handleDownload('pdf')
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <div className="w-4 h-4 text-gray-600">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Download PDF</span>
                  </button>

                  <button
                    onClick={() => {
                      setShowFileMenu(false)
                      handleTifDownloadCurrent()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <div className="w-4 h-4 text-gray-600">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Download TIF Image</span>
                  </button>

                  {/* Video Export - Only show for video templates */}
                  {((template_content?.type || template_content?.mediaType) === 'video') && (
                    <button
                      onClick={() => {
                        setShowFileMenu(false)
                        setShowVideoExportDialog && setShowVideoExportDialog(true)
                      }}
                      disabled={isExportingVideo}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700 disabled:opacity-50"
                    >
                      <div className="w-4 h-4 text-gray-600">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM4 8a1 1 0 000 2h1v3a1 1 0 001 1h4a1 1 0 001-1v-3h1a1 1 0 100-2H4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span>Export Video (MP4)</span>
                    </button>
                  )}

                  {/* Alter Video Duration - Only show for video templates */}
                  {((template_content?.type || template_content?.mediaType) === 'video') && (
                    <button
                      onClick={() => {
                        setShowFileMenu(false)
                        setShowDurationScaleDialog && setShowDurationScaleDialog(true)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                    >
                      <div className="w-4 h-4 text-gray-600">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span>Alter Video Duration</span>
                    </button>
                  )}

                  {/* Bulk AI Image Generate */}
                  {onBulkGenerateAIImages && (
                    <button
                      onClick={() => {
                        setShowFileMenu(false)
                        onBulkGenerateAIImages()
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                    >
                      <div className="w-4 h-4 text-gray-600">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 11-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm9 2a1 1 0 01.894.553l1.382 2.764 2.764 1.382a1 1 0 010 1.788l-2.764 1.382-1.382 2.764a1 1 0 01-1.788 0l-1.382-2.764-2.764-1.382a1 1 0 010-1.788l2.764-1.382 1.382-2.764A1 1 0 0114 4z" />
                        </svg>
                      </div>
                      <span>Bulk AI Image Generate</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setShowFileMenu(false)
                      // Move to trash logic
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Trash2 className="w-4 h-4 text-gray-600" />
                    <span>Move to Trash</span>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={() => {
                      setShowFileMenu(false)
                      const findText = prompt('Find text (word):')
                      if (!findText) return
                      const replaceText = prompt('Replace with:')
                      settemplate_content(prev => ({
                        ...prev,
                        pages: prev.pages.map(p => ({
                          ...p,
                          items: (p.items || []).map(it => it.type === 'text' && typeof it.text === 'string' ? { ...it, text: it.text.split(findText).join(replaceText) } : it)
                        }))
                      }))
                      setHasUnsavedChanges(true)
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <div className="w-4 h-4 text-gray-600">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Find and replace text</span>
                    <span className="ml-auto text-sm text-gray-500">Ctrl+F</span>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={() => {
                      setShowFileMenu(false)
                      handleExit()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <div className="w-4 h-4 text-gray-600">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Exit</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Import Menu */}
          <div className="relative import-menu-container">
            <button
              onClick={() => setShowImportMenu(!showImportMenu)}
              className="flex items-center space-x-1 hover:bg-blue-600 hover:bg-opacity-50 px-3 py-1 rounded transition-all duration-200"
            >
              <Import className="w-3 h-3" />
              <span className="text-xs">Import</span>
              <svg className={`w-3 h-3 transition-transform duration-200 ${showImportMenu ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Import Menu Dropdown */}
            {showImportMenu && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-[600]">
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowImportMenu(false)
                      handleImportPDF && handleImportPDF()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <FileText className="w-4 h-4 text-red-600" />
                    <div className="flex flex-col">
                      <span>Import PDF</span>
                      <span className="text-xs text-gray-500">Convert PDF pages to vector graphics</span>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setShowImportMenu(false)
                      handleImportPSD && handleImportPSD()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Layers className="w-4 h-4 text-blue-600" />
                    <div className="flex flex-col">
                      <span>Import PSD</span>
                      <span className="text-xs text-gray-500">Import Photoshop files with layers</span>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setShowImportMenu(false)
                      handleImportPowerPoint && handleImportPowerPoint()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Presentation className="w-4 h-4 text-orange-600" />
                    <div className="flex flex-col">
                      <span>Import PowerPoint</span>
                      <span className="text-xs text-gray-500">Extract slides and graphics from presentations</span>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setShowImportMenu(false)
                      handleImportCDR && handleImportCDR()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Palette className="w-4 h-4 text-purple-600" />
                    <div className="flex flex-col">
                      <span>Import CorelDRAW</span>
                      <span className="text-xs text-gray-500">Convert CDR files to SVG vector graphics</span>
                    </div>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={() => {
                      setShowImportMenu(false)
                      handleImportLegacyPlatform && handleImportLegacyPlatform()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Copy className="w-4 h-4 text-green-600" />
                    <div className="flex flex-col">
                      <span>Import from Legacy Platform</span>
                      <span className="text-xs text-gray-500">Convert old system JSON to new format</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Action Menu */}
          <div className="relative action-menu-container">
            <button
              onClick={() => setShowActionMenu(!showActionMenu)}
              className="flex items-center space-x-1 hover:bg-blue-600 hover:bg-opacity-50 px-3 py-1 rounded transition-all duration-200"
            >
              <Zap className="w-3 h-3" />
              <span className="text-xs">Action</span>
              <svg className={`w-3 h-3 transition-transform duration-200 ${showActionMenu ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Action Menu Dropdown */}
            {showActionMenu && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-[600]">
                <div className="py-2">
                  {/* Make Private/Public - Admin only */}
                  {isAdmin ? (
                    <>
                      {templateMetadata?.is_public ? (
                        <button
                          onClick={() => {
                            setShowActionMenu(false)
                            handleMakePrivate && handleMakePrivate()
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                        >
                          <EyeOff className="w-4 h-4 text-orange-600" />
                          <span>Make Private</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setShowActionMenu(false)
                            handleMakePublic && handleMakePublic()
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                        >
                          <Globe className="w-4 h-4 text-green-600" />
                          <span>Make Public</span>
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="px-4 py-2 text-gray-500 text-sm flex items-center space-x-3">
                      <Shield className="w-4 h-4" />
                      <span>You don't have permission to make this change</span>
                    </div>
                  )}

                  {/* Generate Sample */}
                  <button
                    onClick={() => {
                      setShowActionMenu(false)
                      handleGenerateSample && handleGenerateSample()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Package className="w-4 h-4 text-blue-600" />
                    <span>Generate Sample</span>
                  </button>

                  {/* Approve/Reject - Admin only */}
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => {
                          setShowActionMenu(false)
                          handleApprove && handleApprove()
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                      >
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Approve</span>
                      </button>

                      <button
                        onClick={() => {
                          setShowActionMenu(false)
                          handleReject && handleReject()
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                      >
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span>Reject</span>
                      </button>
                    </>
                  )}

                  <div className="border-t border-gray-200 my-2"></div>

                  {/* Clone and Open New Design */}
                  <button
                    onClick={() => {
                      setShowActionMenu(false)
                      handleCloneAndOpen && handleCloneAndOpen()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Copy className="w-4 h-4 text-purple-600" />
                    <span>Clone and Open New Design</span>
                  </button>

                  {/* Trigger Sync - Disabled */}
                  <div className="opacity-50 cursor-not-allowed px-4 py-2 flex items-center space-x-3 text-gray-400">
                    <RefreshCw className="w-4 h-4" />
                    <span>Trigger Sync</span>
                    <span className="ml-auto text-xs">Coming Soon</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* View Menu */}
          <div className="relative view-menu-container">
            <button
              onClick={() => setShowViewMenu(!showViewMenu)}
              className="flex items-center space-x-1 hover:bg-blue-600 hover:bg-opacity-50 px-3 py-1 rounded transition-all duration-200"
            >
              <Eye className="w-3 h-3" />
              <span className="text-xs">View</span>
              <svg className={`w-3 h-3 transition-transform duration-200 ${showViewMenu ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* View Menu Dropdown */}
            {showViewMenu && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-[600]">
                <div className="py-2">
                  {/* Generate 9 Different Sample Designs */}
                  <button
                    onClick={() => {
                      setShowViewMenu(false)
                      setShowSampleGenerationDialog(true)
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <Grid3X3 className="w-4 h-4 text-indigo-600" />
                    <div className="flex flex-col">
                      <span>Generate 9 Different Sample Designs</span>
                      <span className="text-xs text-gray-500">Create 3x3 grid with sample data</span>
                    </div>
                  </button>

                  {/* View Past Generated Samples */}
                  <button
                    onClick={() => {
                      setShowViewMenu(false)
                      setShowPastSamplesDialog(true)
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <History className="w-4 h-4 text-blue-600" />
                    <div className="flex flex-col">
                      <span>View Past Generated Samples</span>
                      <span className="text-xs text-gray-500">Browse previously generated samples for this template</span>
                    </div>
                  </button>

                  {/* View AI Generation Queue */}
                  <button
                    onClick={() => {
                      setShowViewMenu(false)
                      setShowAiQueueDialog(true)
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 text-gray-700"
                  >
                    <ListChecks className="w-4 h-4 text-purple-600" />
                    <div className="flex flex-col">
                      <span>View AI Generation Queue</span>
                      <span className="text-xs text-gray-500">AI image generation records for this template with timestamps</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Undo/Redo Buttons */}
          <div className="flex items-center space-x-1 border-l border-blue-400 pl-4 ml-4">
            <button
              onClick={undo}
              disabled={historyIndex <= 0}
              className={`p-2 rounded transition-colors ${
                historyIndex <= 0
                  ? 'text-blue-300 cursor-not-allowed'
                  : 'text-white hover:bg-blue-600'
              }`}
              title="Undo (Ctrl+Z)"
            >
              <Undo className="w-4 h-4" />
            </button>
            <button
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className={`p-2 rounded transition-colors ${
                historyIndex >= history.length - 1
                  ? 'text-blue-300 cursor-not-allowed'
                  : 'text-white hover:bg-blue-600'
              }`}
              title="Redo (Ctrl+Y)"
            >
              <Redo className="w-4 h-4" />
            </button>
          </div>

        </nav>

          {/* Media type indicator only */}
          <div className="flex items-center space-x-2 border-l border-blue-400 pl-4 ml-4">
            <span className="px-2 py-0.5 rounded bg-blue-600/40 text-xs">
              {(template_content?.type || template_content?.mediaType || 'image').toUpperCase()}
            </span>
          </div>

          {/* Save Button - Moved to left for easier access */}
        <button
          onClick={() => saveTemplate(true)}
          disabled={isSaving}
          className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 px-4 py-1 rounded text-sm flex items-center space-x-1 relative"
        >
          {isSaving ? (
            <>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save</span>
            </>
          )}
          {/* Green tick indicator when saved */}
          {isSaved && !isSaving && !hasUnsavedChanges && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full flex items-center justify-center">
              <Check className="w-2 h-2 text-green-600" />
            </div>
          )}
        </button>

      </div>




      <div className="flex items-center space-x-4">
        {/* Template name and category display (read-only) */}
        <div className="flex items-center space-x-2 bg-white/10 border border-white/30 rounded-lg px-3 py-1.5">
          <svg className="w-4 h-4 text-white/70" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-5L9 2H4z" clipRule="evenodd" />
          </svg>
          <div className="flex flex-col">
            {isEditingName ? (
              <div className="flex items-center gap-1">
                <input
                  ref={nameInputRef}
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitEditName() }
                    else if (e.key === 'Escape') { e.preventDefault(); cancelEditName() }
                  }}
                  onBlur={commitEditName}
                  disabled={savingName}
                  maxLength={255}
                  className="text-gray-900 text-sm font-medium rounded px-1.5 py-0.5 w-56 outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="Template name"
                />
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={commitEditName}
                  disabled={savingName}
                  className="text-white/90 hover:text-white p-0.5"
                  title="Save name"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelEditName}
                  disabled={savingName}
                  className="text-white/70 hover:text-white p-0.5"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                className="group flex items-center gap-1.5"
                title={`${currentTemplateName}${
                  (templateMetadata?.Category?.name || templateMetadata?.category)
                    ? `\nCategory: ${templateMetadata?.Category?.name || templateMetadata?.category}` : ''
                }${
                  templateMetadata?.SubCategory?.name ? `\nSubcategory: ${templateMetadata?.SubCategory?.name}` : ''
                }`}
              >
                <span className="text-white text-sm font-medium">
                  {currentTemplateName.length > 30 ? `${currentTemplateName.slice(0, 30)}…` : currentTemplateName}
                </span>
                {onRenameTemplate && (
                  <button
                    onClick={beginEditName}
                    className="text-white/50 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                    title="Rename template"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
            {(templateMetadata?.Category?.name || templateMetadata?.category) && (
              <span className="text-white/70 text-xs">
                {templateMetadata?.Category?.name || templateMetadata?.category || 'General'}
                {templateMetadata?.SubCategory?.name ? ` • ${templateMetadata.SubCategory.name}` : ''}
              </span>
            )}
          </div>
        </div>
        
        

        {/* PowerClip Mode Indicator */}
        {powerClipMode && (
          <div className="bg-blue-100 border border-blue-300 rounded-lg px-3 py-1 flex items-center space-x-2">
            <Package className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-800">PowerClip Mode: Click container to place content</span>
            <button
              onClick={() => {
                setPowerClipMode(false)
                setPowerClipContent(null)
              }}
              className="text-blue-600 hover:text-blue-800"
              title="Cancel PowerClip"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Fullscreen Toggle Button */}
        {setIsFullscreen && (
          <button
            onClick={async () => {
              try {
                if (!isFullscreen) {
                  if (document.documentElement.requestFullscreen) {
                    await document.documentElement.requestFullscreen()
                  } else if (document.documentElement.webkitRequestFullscreen) {
                    await document.documentElement.webkitRequestFullscreen()
                  } else if (document.documentElement.mozRequestFullScreen) {
                    await document.documentElement.mozRequestFullScreen()
                  } else if (document.documentElement.msRequestFullscreen) {
                    await document.documentElement.msRequestFullscreen()
                  }
                } else {
                  if (document.exitFullscreen) {
                    await document.exitFullscreen()
                  } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen()
                  } else if (document.mozCancelFullScreen) {
                    await document.mozCancelFullScreen()
                  } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen()
                  }
                }
              } catch (error) {
                console.error('Error toggling fullscreen:', error)
              }
            }}
            className="flex items-center space-x-2 hover:bg-blue-600 hover:bg-opacity-50 px-3 py-1 rounded transition-all duration-200"
            title={isFullscreen ? 'Exit Fullscreen (F11)' : 'Enter Fullscreen (F11)'}
          >
            <Maximize2 className="w-4 h-4" />
            <span className="hidden sm:inline">{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
          </button>
        )}

        {/* Export Progress toggle (show/hide the export queue panel) */}
        {onToggleExportProgress && (
          <button
            onClick={onToggleExportProgress}
            className="relative flex items-center space-x-2 hover:bg-blue-600 hover:bg-opacity-50 px-3 py-1 rounded transition-all duration-200"
            title={isExportProgressExpanded ? 'Hide Progress Tracker' : 'Show Progress Tracker'}
          >
            <ListChecks className="w-4 h-4" />
            <span className="hidden sm:inline">Progress Tracker</span>
            {exportProgressActiveCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[10px] font-bold rounded-full bg-amber-400 text-gray-900">
                {exportProgressActiveCount}
              </span>
            )}
          </button>
        )}

        {/* Export Menu */}
        <div className="relative" ref={shareDropdownRef}>
          <button
            onClick={() => setShowShareDialog(!showShareDialog)}
            className="flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 px-5 py-2 rounded-lg text-sm font-medium shadow-lg transition-all duration-200 transform hover:scale-105"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
            </svg>
            <span>Export</span>
            <svg className={`w-4 h-4 transition-transform duration-200 ${showShareDialog ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Export & Share Dropdown Menu */}
          {showShareDialog && (
            <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[600] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Export & Share</h3>
                      <p className="text-emerald-100 text-sm">Download or share your design</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Quick Share Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                    </svg>
                    <span>Quick Share</span>
                  </h4>
                  <button
                    onClick={() => copyLinkToClipboard(`${window.location.origin}/shared/${template_content.id || 'design'}`)}
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg transform hover:scale-105"
                  >
                    <Clipboard className="w-4 h-4" />
                    <span className="font-medium">Copy Shareable Link</span>
                  </button>
                </div>

                {/* Export Options Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span>Download Options</span>
                  </h4>

                  {/* Save Project Button */}
                  <div className="mb-4">
                    <button
                      onClick={() => saveTemplate(true)}
                      disabled={isSaving}
                      className="w-full flex items-center space-x-3 p-3 hover:bg-green-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-green-300 group relative"
                    >
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors relative">
                        {isSaving ? (
                          <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Save className="w-5 h-5 text-green-600" />
                        )}
                        {/* Green tick indicator when saved */}
                        {isSaved && !isSaving && !hasUnsavedChanges && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                            <Check className="w-2 h-2 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-gray-800">Save Project</span>
                        <span className="text-xs text-gray-500">Save to cloud</span>
                      </div>
                    </button>
                  </div>

                  {/* Current Page Download */}
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Current Page Download</div>
                    <div className="grid grid-cols-4 gap-2">
                      <button
                        onClick={() => handleDownloadCurrentWithColorMode('jpg')}
                        disabled={downloadingStates.currentJpg || !(template_content?.pages && template_content.pages.length > 0)}
                        className="flex flex-col items-center p-2 hover:bg-orange-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-orange-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors mb-1">
                          {downloadingStates.currentJpg ? (
                            <div className="w-3 h-3 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <div className="w-4 h-4 text-orange-600">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-800">JPG</span>
                      </button>

                      <button
                        onClick={() => handleDownloadCurrentWithColorMode('png')}
                        disabled={downloadingStates.currentPng || !(template_content?.pages && template_content.pages.length > 0)}
                        className="flex flex-col items-center p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-green-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors mb-1">
                          {downloadingStates.currentPng ? (
                            <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <div className="w-4 h-4 text-green-600">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-800">PNG</span>
                      </button>

                      <button
                        onClick={() => handleDownloadCurrentWithColorMode('pdf')}
                        disabled={downloadingStates.currentPdf || !(template_content?.pages && template_content.pages.length > 0)}
                        className="flex flex-col items-center p-2 hover:bg-red-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-red-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors mb-1">
                          {downloadingStates.currentPdf ? (
                            <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <FileText className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-800">PDF</span>
                      </button>

                      <button
                        onClick={() => handleTifDownloadCurrent()}
                        disabled={downloadingStates.currentTif || !(template_content?.pages && template_content.pages.length > 0)}
                        className="flex flex-col items-center p-2 hover:bg-blue-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-blue-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors mb-1">
                          {downloadingStates.currentTif ? (
                            <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <div className="w-4 h-4 text-blue-600">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-800">TIF</span>
                      </button>
                    </div>
                  </div>

                  {/* All Pages Download */}
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">All Pages Download</div>
                    <div className="grid grid-cols-4 gap-2">
                      <button
                        onClick={() => handleDownloadFullWithColorMode('jpg')}
                        disabled={downloadingStates.fullJpg || !(template_content?.pages && template_content.pages.length > 0)}
                        className="flex flex-col items-center p-2 hover:bg-orange-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-orange-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors mb-1">
                          {downloadingStates.fullJpg ? (
                            <div className="w-3 h-3 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <div className="w-4 h-4 text-orange-600">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-800">JPG</span>
                      </button>

                      <button
                        onClick={() => handleDownloadFullWithColorMode('png')}
                        disabled={downloadingStates.fullPng || !(template_content?.pages && template_content.pages.length > 0)}
                        className="flex flex-col items-center p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-green-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors mb-1">
                          {downloadingStates.fullPng ? (
                            <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <div className="w-4 h-4 text-green-600">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-800">PNG</span>
                      </button>

                      <button
                        onClick={() => handleDownloadFullWithColorMode('pdf')}
                        disabled={downloadingStates.fullPdf || !(template_content?.pages && template_content.pages.length > 0)}
                        className="flex flex-col items-center p-2 hover:bg-red-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-red-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors mb-1">
                          {downloadingStates.fullPdf ? (
                            <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <FileText className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-800">PDF</span>
                      </button>

                      <button
                        onClick={() => handleTifDownloadFull()}
                        disabled={downloadingStates.fullTif || !(template_content?.pages && template_content.pages.length > 0)}
                        className="flex flex-col items-center p-2 hover:bg-blue-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-blue-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors mb-1">
                          {downloadingStates.fullTif ? (
                            <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <div className="w-4 h-4 text-blue-600">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-800">TIF</span>
                      </button>
                    </div>
                  </div>

                  {/* Video Export Button - Only show for video templates */}
                  {((template_content?.type || template_content?.mediaType) === 'video') && (
                    <div className="mb-4">
                      <button
                        onClick={() => setShowVideoExportDialog && setShowVideoExportDialog(true)}
                        disabled={isExportingVideo || !(template_content?.pages && template_content.pages.length > 0)}
                        className="w-full flex items-center space-x-3 p-3 hover:bg-purple-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-purple-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                          {isExportingVideo ? (
                            <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <div className="w-5 h-5 text-purple-600">
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 6a3 3 0 013-3h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H5a3 3 0 01-3-3V6zM5 6a1 1 0 000 2v6a1 1 0 001 1h6a1 1 0 001-1V8a1 1 0 100-2H5z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="text-sm font-medium text-gray-800">
                            {isExportingVideo ? 'Exporting Video...' : 'Export Video'}
                          </span>
                          <span className="text-xs text-gray-500">Generate MP4 video</span>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Public link disabled */}

                  {/* Print disabled */}

                  {/* WhatsApp disabled */}
                </div>

                {/* Additional Actions Section */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    <span>More Actions</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => navigate('/dashboard?tab=templates')}
                      className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 group"
                    >
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                        <Clipboard className="w-4 h-4 text-gray-600" />
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-gray-800">Templates</span>
                        <span className="text-xs text-gray-500">Browse library</span>
                      </div>
                    </button>

                    <button
                      onClick={() => copyLinkToClipboard(window.location.href)}
                      className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 group"
                    >
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                        <Copy className="w-4 h-4 text-gray-600" />
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-gray-800">Copy URL</span>
                        <span className="text-xs text-gray-500">Current page</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      {showExitConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Unsaved Changes</h3>
              <p className="text-gray-600 mb-6">You have unsaved changes. Do you want to save before closing?</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowExitConfirmation(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmedExitWithoutSave}
                  className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Don't Save
                </button>
                <button
                  onClick={handleConfirmedExitWithSave}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Save & Exit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resize Page Dialog */}
      {showResizeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resize Page</h3>
              <p className="text-gray-600 mb-6">
                Enter new dimensions for the page. All items will be scaled proportionally.
              </p>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Width (px)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={resizeConfig.width}
                      onChange={(e) => {
                        const value = e.target.value
                        const newWidth = value === '' ? '' : Math.max(1, parseInt(value) || 1)
                        setResizeConfig(prev => {
                          if (prev.maintainAspectRatio && currentPage?.page_config && newWidth !== '') {
                            const aspectRatio = (currentPage.page_config.height || 600) / (currentPage.page_config.width || 800)
                            return {
                              ...prev,
                              width: newWidth,
                              height: Math.round(newWidth * aspectRatio)
                            }
                          }
                          return { ...prev, width: newWidth }
                        })
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || parseInt(e.target.value) < 1) {
                          setResizeConfig(prev => ({ ...prev, width: 1 }))
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Height (px)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={resizeConfig.height}
                      onChange={(e) => {
                        const value = e.target.value
                        const newHeight = value === '' ? '' : Math.max(1, parseInt(value) || 1)
                        setResizeConfig(prev => {
                          if (prev.maintainAspectRatio && currentPage?.page_config && newHeight !== '') {
                            const aspectRatio = (currentPage.page_config.width || 800) / (currentPage.page_config.height || 600)
                            return {
                              ...prev,
                              height: newHeight,
                              width: Math.round(newHeight * aspectRatio)
                            }
                          }
                          return { ...prev, height: newHeight }
                        })
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || parseInt(e.target.value) < 1) {
                          setResizeConfig(prev => ({ ...prev, height: 1 }))
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="maintainAspectRatio"
                    checked={resizeConfig.maintainAspectRatio}
                    onChange={(e) => setResizeConfig(prev => ({ ...prev, maintainAspectRatio: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="maintainAspectRatio" className="ml-2 block text-sm text-gray-700">
                    Maintain aspect ratio
                  </label>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Current:</strong> {currentPage?.page_config?.width || 800} × {currentPage?.page_config?.height || 600} px
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>New:</strong> {resizeConfig.width} × {resizeConfig.height} px
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowResizeDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmResize}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Resize Page
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EditorNavigation
